// ════════════════════════════════════════════════════════════
// Dialog ECR — primitive modale unifiée (Radix + Vaul)
// ════════════════════════════════════════════════════════════
// Sur desktop (≥768px) OU si `mobileVariant !== 'bottom-sheet'` :
//   → Radix Dialog (modal centré / fullscreen).
// Sur mobile (<768px) ET `mobileVariant === 'bottom-sheet'` (défaut) :
//   → Vaul Drawer (vrai bottom sheet avec swipe-to-dismiss natif).
//
// ⚠️ Déviation spec : `mobileVariant` est sur Dialog ROOT (pas
// DialogContent) parce qu'on doit choisir la primitive racine
// (Radix ou Vaul) AVANT de rendre les enfants. Documenté dans le
// récap PR.
// ════════════════════════════════════════════════════════════
import * as React from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { Drawer as VaulDrawer } from 'vaul';
import { motion, type useAnimationControls } from 'framer-motion';
import { X } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/components/ui/table/hooks/use-media-query';
import { backdropOpacityClass, useModalStack } from './hooks/use-modal-stack';
import { usePreventClose, type PreventCloseFn } from './hooks/use-prevent-close';

// ─── Contexte interne ────────────────────────────────────────

type DialogMode = 'radix' | 'vaul';
type MobileVariant = 'centered' | 'bottom-sheet' | 'fullscreen';

interface DialogContextValue {
  mode: DialogMode;
  mobileVariant: MobileVariant;
  shakeControls: ReturnType<typeof useAnimationControls>;
  isMobile: boolean;
  /** Évalue preventClose puis propage la fermeture si autorisée. */
  requestClose: () => void;
  /** True si preventClose est défini (l'utilisateur a configuré un garde-fou). */
  hasPreventClose: boolean;
}

const DialogContext = React.createContext<DialogContextValue | null>(null);

function useDialogContext(): DialogContextValue {
  const ctx = React.useContext(DialogContext);
  if (!ctx) {
    throw new Error("Les sous-composants Dialog doivent être enfants d'un <Dialog> root.");
  }
  return ctx;
}

// ─── Sizes ───────────────────────────────────────────────────

const dialogContentSizes = {
  sm: 'sm:max-w-sm', // ~384
  md: 'sm:max-w-md', // ~448 (proche des 512 spec — on prend la valeur Tailwind alignée)
  lg: 'sm:max-w-lg', // ~512
  xl: 'sm:max-w-xl', // ~576
  full: 'sm:max-w-[calc(100vw-2rem)]',
} as const;

type DialogSize = keyof typeof dialogContentSizes;

// ─── Root ────────────────────────────────────────────────────

export interface DialogProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Variant utilisé sur viewport mobile (<768px). Défaut : `bottom-sheet`.
   *
   * **Doit être défini au niveau root** (pas sur `DialogContent`) car cette
   * valeur détermine le wrapper sous-jacent (Vaul Drawer vs Radix Dialog)
   * avant le rendu des enfants — la changer dynamiquement remonterait
   * l'arbre. Sémantiquement équivalent à la spec qui la plaçait sur Content.
   *
   * - `centered` — modal centré (même rendu que desktop)
   * - `bottom-sheet` — bottom sheet Vaul natif (swipe-to-dismiss)
   * - `fullscreen` — Radix Dialog plein écran (slide-up depuis le bas)
   */
  mobileVariant?: MobileVariant;
  /** Garde-fou async : retourne `false` pour bloquer la fermeture + shake. */
  preventClose?: PreventCloseFn;
  children: React.ReactNode;
}

/**
 * Dialog ECR — modale centrée (desktop) / bottom-sheet Vaul (mobile défaut).
 *
 * @remarks
 * - 8 sous-composants : DialogTrigger · DialogContent · DialogHeader ·
 *   DialogTitle · DialogDescription · DialogFooter · DialogClose · DialogOverlay.
 * - Stack illimité géré globalement (cf. `useModalStack`) : backdrop
 *   progressif /40 → /50 → /60.
 * - Focus trap + scroll lock + scrollbar compensation : Radix natif.
 * - Vaul (bottom-sheet mobile) gère le swipe-to-dismiss + back button Android.
 */
function Dialog({
  open,
  defaultOpen,
  onOpenChange,
  mobileVariant = 'bottom-sheet',
  preventClose,
  children,
}: DialogProps) {
  const isMobile = useIsMobile();
  const useVaul = isMobile && mobileVariant === 'bottom-sheet';
  const { handleOpenChange, requestClose, shakeControls, hasPreventClose } = usePreventClose({
    preventClose,
    onOpenChange,
  });

  const ctx = React.useMemo<DialogContextValue>(
    () => ({
      mode: useVaul ? 'vaul' : 'radix',
      mobileVariant,
      shakeControls,
      isMobile,
      requestClose,
      hasPreventClose,
    }),
    [useVaul, mobileVariant, shakeControls, isMobile, requestClose, hasPreventClose],
  );

  if (useVaul) {
    return (
      <DialogContext.Provider value={ctx}>
        <VaulDrawer.Root open={open} defaultOpen={defaultOpen} onOpenChange={handleOpenChange}>
          {children}
        </VaulDrawer.Root>
      </DialogContext.Provider>
    );
  }

  return (
    <DialogContext.Provider value={ctx}>
      <RadixDialog.Root open={open} defaultOpen={defaultOpen} onOpenChange={handleOpenChange}>
        {children}
      </RadixDialog.Root>
    </DialogContext.Provider>
  );
}

// ─── Trigger ─────────────────────────────────────────────────

const DialogTrigger = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Trigger>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Trigger>
>((props, ref) => {
  const { mode } = useDialogContext();
  if (mode === 'vaul') {
    return <VaulDrawer.Trigger ref={ref} {...props} />;
  }
  return <RadixDialog.Trigger ref={ref} {...props} />;
});
DialogTrigger.displayName = 'DialogTrigger';

// ─── Close ───────────────────────────────────────────────────

const DialogClose = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Close>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Close>
>((props, ref) => {
  const { mode } = useDialogContext();
  if (mode === 'vaul') {
    return <VaulDrawer.Close ref={ref} {...props} />;
  }
  return <RadixDialog.Close ref={ref} {...props} />;
});
DialogClose.displayName = 'DialogClose';

// ─── Overlay ─────────────────────────────────────────────────
// Exposé pour customisation rare. Normalement géré automatiquement
// par DialogContent.

const dialogOverlayClass = cva(
  'fixed inset-0 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 motion-reduce:animate-none',
);

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Overlay>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Overlay> & { depth?: number }
>(({ className, depth = 1, ...props }, ref) => {
  const { mode } = useDialogContext();
  const opacityClass = backdropOpacityClass(depth);
  if (mode === 'vaul') {
    return (
      <VaulDrawer.Overlay
        ref={ref}
        className={cn('fixed inset-0 z-50 motion-reduce:animate-none', opacityClass, className)}
        {...props}
      />
    );
  }
  return (
    <RadixDialog.Overlay
      ref={ref}
      className={cn(dialogOverlayClass(), opacityClass, className)}
      {...props}
    />
  );
});
DialogOverlay.displayName = 'DialogOverlay';

// ─── Content ─────────────────────────────────────────────────

const dialogContentVariants = cva(
  'fixed z-50 grid w-full gap-0 bg-card text-card-foreground shadow-2xl outline-none focus:outline-none',
  {
    variants: {
      placement: {
        // Centré (desktop par défaut OU mobileVariant=centered)
        // max-w garantit qu'à <420 px le dialog laisse au moins 16 px de
        // gouttière de chaque côté, sinon les boutons du footer débordent.
        centered:
          'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-lg max-h-[85vh] max-w-[calc(100vw-2rem)] sm:max-w-md ' +
          'data-[state=open]:animate-in data-[state=closed]:animate-out ' +
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 ' +
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 ' +
          'data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 ' +
          'duration-200 motion-reduce:animate-none motion-reduce:duration-0',
        // Fullscreen (mobile uniquement, mobileVariant=fullscreen)
        fullscreen:
          'inset-0 rounded-none max-h-screen ' +
          'data-[state=open]:animate-in data-[state=closed]:animate-out ' +
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 ' +
          'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom ' +
          'duration-300 motion-reduce:animate-none motion-reduce:duration-0',
      },
    },
    defaultVariants: { placement: 'centered' },
  },
);

export interface DialogContentProps
  extends
    Omit<React.ComponentPropsWithoutRef<typeof RadixDialog.Content>, 'onOpenAutoFocus'>,
    VariantProps<typeof dialogContentVariants> {
  size?: DialogSize;
  dismissibleEscape?: boolean;
  dismissibleOverlay?: boolean;
  closeButton?: boolean;
  /** Désactive l'auto-focus initial Radix (utile si focus custom). */
  disableAutoFocus?: boolean;
}

const DialogContent = React.forwardRef<HTMLDivElement, DialogContentProps>(
  (
    {
      className,
      children,
      size = 'md',
      dismissibleEscape = true,
      dismissibleOverlay = true,
      closeButton = true,
      disableAutoFocus = false,
      ...props
    },
    ref,
  ) => {
    const { mode, mobileVariant, shakeControls, isMobile, requestClose, hasPreventClose } =
      useDialogContext();
    const open = true; // mounted = open
    const stack = useModalStack(open);

    // Vaul (bottom-sheet mobile)
    if (mode === 'vaul') {
      return (
        <VaulDrawer.Portal>
          <VaulDrawer.Overlay
            className={cn('fixed inset-0 z-50', backdropOpacityClass(stack.depth))}
            // Vaul gère le click overlay nativement, mais on peut désactiver via setDismissible
          />
          <VaulDrawer.Content
            ref={ref}
            className={cn(
              'bg-card text-card-foreground fixed inset-x-0 bottom-0 z-50 mt-24 flex h-auto max-h-[90vh] flex-col rounded-t-2xl shadow-2xl outline-none',
              className,
            )}
            onEscapeKeyDown={(e) => {
              if (!dismissibleEscape) {
                e.preventDefault();
                return;
              }
              if (hasPreventClose) {
                // Bloque la fermeture native de Vaul/Radix, on évalue
                // nous-mêmes preventClose puis on propage si autorisé.
                e.preventDefault();
                requestClose();
              }
            }}
            onOpenAutoFocus={disableAutoFocus ? (e) => e.preventDefault() : undefined}
            {...props}
          >
            {/* Drag handle iOS-like — VaulDrawer.Handle s'auto-bind aux
                gestures pointer (drag-down/up + snap points), même quand
                le body intérieur a overflow-y-auto. */}
            <VaulDrawer.Handle
              data-testid="vaul-drawer-handle"
              className="bg-muted-foreground/30 mx-auto mt-2 h-1 w-9 shrink-0 rounded-full"
            />
            <div className="relative flex max-h-[calc(90vh-2rem)] flex-col overflow-hidden">
              {children}
              {closeButton ? (
                <VaulDrawer.Close
                  className={cn(
                    'text-muted-foreground absolute right-4 top-4 rounded-md p-1.5 transition-opacity hover:opacity-100',
                    'focus:ring-ring opacity-70 focus:outline-none focus:ring-2 focus:ring-offset-2',
                    'motion-reduce:transition-none',
                  )}
                  aria-label="Fermer"
                >
                  <X className="h-4 w-4" />
                </VaulDrawer.Close>
              ) : null}
            </div>
          </VaulDrawer.Content>
        </VaulDrawer.Portal>
      );
    }

    // Radix Dialog
    const placement: 'centered' | 'fullscreen' =
      isMobile && mobileVariant === 'fullscreen' ? 'fullscreen' : 'centered';

    // L'overlay opaque est rendu UNIQUEMENT pour le modal le plus bas du stack
    // (index 0) — les modals au-dessus rendent un overlay transparent pour
    // éviter la superposition d'assombrissements CSS.
    const showOpaqueBackdrop = stack.index === 0;

    return (
      <RadixDialog.Portal>
        <RadixDialog.Overlay
          className={cn(
            'fixed inset-0 z-50 transition-colors duration-200',
            showOpaqueBackdrop ? backdropOpacityClass(stack.depth) : 'bg-transparent',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'motion-reduce:animate-none motion-reduce:duration-0',
          )}
        />
        <RadixDialog.Content
          ref={ref}
          asChild
          onEscapeKeyDown={(e) => {
            if (!dismissibleEscape) {
              e.preventDefault();
              return;
            }
            if (hasPreventClose) {
              // Bloque la fermeture native Radix : on évalue preventClose
              // nous-mêmes (sync ou async) puis on propage si autorisé.
              // Sans ça, Radix appelle onOpenChange(false) qui passe par
              // notre wrapper, mais en cas de race ça peut court-circuiter.
              e.preventDefault();
              requestClose();
            }
          }}
          onPointerDownOutside={(e) => {
            if (!dismissibleOverlay) {
              e.preventDefault();
              return;
            }
            if (hasPreventClose) {
              e.preventDefault();
              requestClose();
            }
          }}
          onInteractOutside={(e) => {
            if (!dismissibleOverlay) {
              e.preventDefault();
              return;
            }
            if (hasPreventClose) {
              e.preventDefault();
              requestClose();
            }
          }}
          onOpenAutoFocus={disableAutoFocus ? (e) => e.preventDefault() : undefined}
          {...props}
        >
          <motion.div
            animate={shakeControls}
            className={cn(
              dialogContentVariants({ placement }),
              placement === 'centered' && dialogContentSizes[size],
              className,
            )}
          >
            {children}
            {closeButton ? (
              <RadixDialog.Close
                className={cn(
                  'text-muted-foreground absolute right-4 top-4 rounded-md p-1.5 transition-opacity hover:opacity-100',
                  'focus:ring-ring opacity-70 focus:outline-none focus:ring-2 focus:ring-offset-2',
                  'motion-reduce:transition-none',
                )}
                aria-label="Fermer"
              >
                <X className="h-4 w-4" />
              </RadixDialog.Close>
            ) : null}
          </motion.div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    );
  },
);
DialogContent.displayName = 'DialogContent';

// ─── Header / Body / Footer ──────────────────────────────────

// Padding-x adaptatif : 16 px sur très petit viewport (<420 px), 24 px ailleurs.
// Garantit qu'on garde 8 px de marge intérieure aux boutons du footer même
// quand la viewport est minuscule (iPhone SE 320 px notamment).
const RESPONSIVE_PADDING_X = 'px-4 sm:px-6';

const DialogHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'border-border flex flex-col gap-1.5 border-b py-5 text-left',
        RESPONSIVE_PADDING_X,
        className,
      )}
      {...props}
    />
  ),
);
DialogHeader.displayName = 'DialogHeader';

/**
 * DialogBody — slot scrollable entre Header (sticky top) et Footer (sticky bottom).
 * Ajout maison pour gérer proprement le scroll interne sans casser les borders.
 */
const DialogBody = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex-1 overflow-y-auto py-5', RESPONSIVE_PADDING_X, className)}
      {...props}
    />
  ),
);
DialogBody.displayName = 'DialogBody';

const DialogFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        // <420 px : boutons empilés en colonne, full-width pour rester lisibles.
        // ≥420 px : ligne, alignés à droite (comportement standard).
        'border-border flex flex-col-reverse gap-2 border-t py-4',
        RESPONSIVE_PADDING_X,
        'min-[420px]:flex-row min-[420px]:justify-end min-[420px]:gap-2',
        '[&>button]:w-full min-[420px]:[&>button]:w-auto',
        className,
      )}
      {...props}
    />
  ),
);
DialogFooter.displayName = 'DialogFooter';

// ─── Title / Description ─────────────────────────────────────

const DialogTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => {
    const { mode } = useDialogContext();
    const Comp = mode === 'vaul' ? VaulDrawer.Title : RadixDialog.Title;
    return (
      <Comp
        ref={ref}
        className={cn('text-base font-semibold leading-tight tracking-tight', className)}
        {...props}
      />
    );
  },
);
DialogTitle.displayName = 'DialogTitle';

const DialogDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const { mode } = useDialogContext();
  const Comp = mode === 'vaul' ? VaulDrawer.Description : RadixDialog.Description;
  return <Comp ref={ref} className={cn('text-muted-foreground text-sm', className)} {...props} />;
});
DialogDescription.displayName = 'DialogDescription';

export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose,
  DialogOverlay,
};
